import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { 
  heroContent, 
  conceptBlocks, 
  whyNuFeatures, 
  testimonials,
  membershipContent,
  locationContent 
} from '../../mocks/content';
import { shopProducts } from '../../mocks/shop-products';

export default function HomePage() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: 'pilates'
  });

  const handleTestimonialChange = (direction: 'prev' | 'next') => {
    if (direction === 'next') {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    } else {
      setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you for your interest! We will be in touch soon.');
    setFormData({ name: '', email: '', phone: '', interest: 'pilates' });
  };

  // Get featured products (first 4 products)
  const featuredProducts = shopProducts.slice(0, 4);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/cb19e1387ff677f38217194099f413b9.jpg"
            alt="RE:SET Wellness Sanctuary"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        </div>

        <div className="relative z-10 text-center px-6 md:px-12 max-w-6xl mx-auto py-32 w-full">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-10 tracking-tight leading-tight">
            {heroContent.headline}
          </h1>
          <p className="text-lg md:text-2xl text-white/95 tracking-wide mb-6 max-w-4xl mx-auto leading-relaxed font-medium">
            {heroContent.subheadline}
          </p>
          <p className="text-base md:text-lg text-white/90 tracking-wide mb-14 max-w-4xl mx-auto leading-relaxed font-light">
            {heroContent.tagline}
          </p>
          <Button variant="primary">{heroContent.cta}</Button>
        </div>
      </section>

      {/* Concept Blocks */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-reset-bg-alt">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-20">
            <p className="text-xs uppercase tracking-widest text-reset-muted mb-10 font-semibold">
              THREE EXPERIENCES
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-reset-dark mb-8 tracking-tight uppercase">
              One Sanctuary
            </h2>
          </div>

          <div className="space-y-24 md:space-y-32">
            {conceptBlocks.map((block, index) => (
              <div
                key={block.id}
                className={`grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center ${
                  index % 2 === 1 ? 'md:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                  <p className="text-xs uppercase tracking-widest text-reset-muted mb-6 font-semibold">
                    {block.label}
                  </p>
                  <h3 className="text-3xl md:text-4xl font-bold text-reset-dark mb-6 tracking-tight uppercase">
                    {block.title}
                  </h3>
                  <p className="text-base text-reset-dark leading-relaxed mb-6">
                    {block.description}
                  </p>
                  <p className="text-sm text-reset-dark leading-relaxed mb-8">
                    {block.details}
                  </p>
                  <a
                    href={block.id === 'pilates' ? '/reformer-pilates' : `/#${block.id}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-reset-sage hover:text-[#4A6278] transition-colors duration-300 uppercase tracking-wide cursor-pointer"
                  >
                    {block.link}
                    <i className="ri-arrow-right-line text-lg"></i>
                  </a>
                </div>
                <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                  <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-lg">
                    <img
                      src={block.image}
                      alt={block.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RE:SET Essentials - Featured Products */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-reset-grey">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-reset-muted mb-8 font-semibold">
              SHOP
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-reset-dark mb-6 tracking-tight uppercase">
              RE:SET Essentials
            </h2>
            <p className="text-base text-reset-dark leading-relaxed max-w-2xl mx-auto">
              Quiet essentials designed for everyday restoration
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {featuredProducts.map((product) => (
              <a
                key={product.id}
                href={`/shop/product/${product.id}`}
                className="group cursor-pointer"
              >
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-base font-bold text-reset-dark mb-2 uppercase tracking-wide">
                      {product.name}
                    </h3>
                    <p className="text-sm text-reset-muted mb-4 line-clamp-2">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-reset-dark">
                        £{product.price.toFixed(2)}
                      </span>
                      <span className="text-xs uppercase tracking-wide text-reset-sage group-hover:text-[#4A6278] transition-colors duration-300">
                        View Details
                      </span>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>

          <div className="text-center">
            <a
              href="/shop"
              className="inline-flex items-center gap-2 text-sm font-semibold text-reset-sage hover:text-[#4A6278] transition-colors duration-300 uppercase tracking-wide cursor-pointer"
            >
              View All Products
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-reset-bg-alt">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
            <div className="md:col-span-1 flex items-start justify-start md:justify-center">
              <p className="text-xs uppercase tracking-widest text-reset-muted md:rotate-90 md:origin-top-left md:translate-y-8 font-semibold">
                WHY RE:SET
              </p>
            </div>
            <div className="md:col-span-11">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-reset-dark mb-12 leading-relaxed tracking-tight max-w-3xl uppercase">
                Renewal. Regeneration. Return.
              </h2>
              <div className="space-y-8 max-w-3xl">
                <p className="text-base text-reset-dark leading-relaxed">
                  RE:SET was created as a modern sanctuary for those seeking to pause, restore, and return to everyday life renewed. We believe wellness isn't about trends or quick fixes—it's about sustainable practices, nervous system regulation, and long-term vitality.
                </p>
                <p className="text-base text-reset-dark leading-relaxed">
                  We're not a gym. We're not a beauty clinic. We're not chasing the next wellness trend. RE:SET is a place to escape the noise, reset your nervous system, and build rituals that support longevity and regeneration.
                </p>
                <p className="text-base text-reset-dark leading-relaxed">
                  This is a sanctuary designed for depth, consistency, and return. A place where you can pause, breathe, and reconnect with yourself—again and again.
                </p>
                <p className="text-base text-reset-muted leading-relaxed italic font-medium">
                  "Longevity-led, not trend-led. A place to return to yourself."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why RE:SET Features */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-reset-grey">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-reset-dark text-center mb-20 tracking-tight uppercase">
            Why RE:SET is Different
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-12 md:gap-16">
            {whyNuFeatures.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-8">
                  <i className={`${feature.icon} text-4xl text-reset-sage`}></i>
                </div>
                <h3 className="text-base font-bold text-reset-dark mb-5 tracking-tight uppercase">
                  {feature.title}
                </h3>
                <p className="text-sm text-reset-dark leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-reset-bg-alt">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
            <div className="md:col-span-4">
              <div className="aspect-[3/4] rounded-3xl overflow-hidden max-w-md mx-auto md:max-w-none">
                <img
                  src={testimonials[currentTestimonial].image}
                  alt={testimonials[currentTestimonial].author}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="md:col-span-8">
              <p className="text-xs uppercase tracking-widest text-reset-muted mb-8 font-semibold">
                MEMBER EXPERIENCE
              </p>
              <blockquote className="text-2xl md:text-3xl font-medium text-reset-dark leading-relaxed mb-12">
                <span className="text-reset-sage">"</span>
                {testimonials[currentTestimonial].quote}
                <span className="text-reset-sage">"</span>
              </blockquote>
              <p className="text-sm font-bold text-reset-dark mb-12 uppercase tracking-wide">
                — {testimonials[currentTestimonial].author}, {testimonials[currentTestimonial].location}
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => handleTestimonialChange('prev')}
                  className="w-12 h-12 min-h-[44px] rounded-lg border-[1.5px] border-[#7F9C8B] flex items-center justify-center text-[#7F9C8B] hover:bg-[#DDE6E1] hover:border-[#5E7568] hover:text-[#5E7568] transition-all duration-200 ease-in-out active:scale-[0.98] cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-xl w-5 h-5 flex items-center justify-center"></i>
                </button>
                <button
                  onClick={() => handleTestimonialChange('next')}
                  className="w-12 h-12 min-h-[44px] rounded-lg border-[1.5px] border-[#7F9C8B] flex items-center justify-center text-[#7F9C8B] hover:bg-[#DDE6E1] hover:border-[#5E7568] hover:text-[#5E7568] transition-all duration-200 ease-in-out active:scale-[0.98] cursor-pointer"
                >
                  <i className="ri-arrow-right-line text-xl w-5 h-5 flex items-center justify-center"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Section */}
      <section id="memberships" className="py-24 md:py-32 px-6 md:px-12 bg-reset-blue-light">
        <div className="max-w-[1000px] mx-auto text-center">
          <p className="text-xs uppercase tracking-widest text-reset-muted mb-8 font-semibold">
            BUILD YOUR RITUAL
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-reset-dark mb-10 tracking-tight leading-relaxed uppercase">
            {membershipContent.headline}
          </h2>
          <p className="text-base text-reset-dark leading-relaxed mb-14 max-w-2xl mx-auto">
            {membershipContent.intro}
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button variant="primary">Register Interest</Button>
            <Button variant="secondary">Explore Memberships</Button>
          </div>
        </div>
      </section>

      {/* Location & Visit */}
      <section id="visit" className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-20">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-reset-dark mb-10 tracking-tight uppercase">
                Visit RE:SET
              </h2>
              <div className="space-y-6 mb-12">
                <div>
                  <p className="text-base text-reset-dark leading-relaxed">
                    {locationContent.address}
                  </p>
                  <p className="text-base text-reset-dark leading-relaxed">
                    {locationContent.street}
                  </p>
                  <p className="text-base text-reset-dark leading-relaxed">
                    {locationContent.city}
                  </p>
                </div>
                <p className="text-sm text-reset-muted">
                  {locationContent.transport}
                </p>
              </div>

              <h3 className="text-2xl font-bold text-reset-dark mb-8 tracking-tight uppercase">
                Register Your Interest
              </h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <input
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-white border-2 border-reset-grey rounded-full px-6 py-4 text-sm text-reset-dark placeholder-reset-muted focus:outline-none focus:border-reset-sage transition-colors duration-300"
                  required
                />
                <input
                  type="email"
                  placeholder="Your email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-white border-2 border-reset-grey rounded-full px-6 py-4 text-sm text-reset-dark placeholder-reset-muted focus:outline-none focus:border-reset-sage transition-colors duration-300"
                  required
                />
                <input
                  type="tel"
                  placeholder="Your phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full bg-white border-2 border-reset-grey rounded-full px-6 py-4 text-sm text-reset-dark placeholder-reset-muted focus:outline-none focus:border-reset-sage transition-colors duration-300"
                  required
                />
                <select
                  value={formData.interest}
                  onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                  className="w-full bg-white border-2 border-reset-grey rounded-full px-6 py-4 text-sm text-reset-dark focus:outline-none focus:border-reset-sage transition-colors duration-300 cursor-pointer"
                >
                  <option value="pilates">Reformer Pilates</option>
                  <option value="thermal">Thermal Suites</option>
                  <option value="membership">Membership</option>
                  <option value="cafe">Café</option>
                </select>
                <Button type="submit" variant="primary" fullWidth>
                  Submit Interest
                </Button>
              </form>
            </div>

            <div>
              <div className="aspect-video rounded-3xl overflow-hidden shadow-lg">
                <iframe
                  src={locationContent.mapEmbed}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="RE:SET Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}